import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, DestroyRef, HostListener, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import {
  TableComponent,
  TableFiltersTemplateDirective,
  TableSearchComponent,
  TableSearchTemplateDirective,
  TableTopBarTemplateDirective,
} from '@next/core-lib/table-v2';
import { Store } from '@ngrx/store';

import { catchError, finalize, map, of, switchMap } from 'rxjs';

import { FeedBalanceDataSource } from './feed-balance.data-source';

import { LayerBackendService } from '../../../shared/services/layer-backend.service';
import {
  FeedBalance,
  FeedBalanceRow,
  FeedBalanceService,
  SaveFeedBalance,
} from '../../services/feed-balance.service';
import { setSelectedFlockId } from '../../store/actions/flock.actions';
import { getFlocks, getSelectedFlockId } from '../../store/selectors/flock.selectors';

@Component({
  selector: 'app-feed-balance',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    TableComponent,
    TableFiltersTemplateDirective,
    TableSearchComponent,
    TableSearchTemplateDirective,
    TableTopBarTemplateDirective,
    TranslatePipe,
  ],
  templateUrl: './feed-balance.component.html',
  styleUrl: './feed-balance.component.scss',
})
export class FeedBalanceComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly service = inject(FeedBalanceService);
  private readonly backend = inject(LayerBackendService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly selectedFlockId = toSignal(this.store.select(getSelectedFlockId));
  readonly flocks = toSignal(this.store.select(getFlocks), { initialValue: [] });
  readonly balance = signal<FeedBalance | null>(null);
  readonly rows = signal<FeedBalanceRow[]>([]);
  readonly search = signal('');
  readonly showAllDates = signal(false);
  private readonly customPeriod = signal<{ start: string; end: string } | null>(null);
  readonly defaultPeriod = computed(() => {
    const flock = this.flocks().find((flock) => flock.id === this.balance()?.flockId);
    const today = new Date();
    const localToday = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, '0'),
      String(today.getDate()).padStart(2, '0'),
    ].join('-');
    return {
      start: flock?.arrival?.arrivalDate?.slice(0, 10) ?? '',
      end: flock ? flock.departure?.departureDate?.slice(0, 10) || localToday : '',
    };
  });
  readonly period = computed(() => this.customPeriod() ?? this.defaultPeriod());
  readonly pickerPeriod = computed(() => ({
    start: this.period().start ? new Date(`${this.period().start}T00:00:00`) : null,
    end: this.period().end ? new Date(`${this.period().end}T00:00:00`) : null,
  }));
  private readonly savedRowIds = computed(
    () =>
      new Set(
        this.balance()
          ?.rows.filter((row) => row.usedKg != null && row.usedKg > 0)
          .map((row) => row.deliveryLineId) ?? [],
      ),
  );
  readonly columns = [
    'deliveryDate',
    'externalDeliveryId',
    'externalOrderId',
    'feed',
    'delivered',
    'usedKg',
  ];
  readonly visibleRows = computed(() => {
    const terms = this.search().trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
    return this.rows().filter((row) => {
      const text = [
        row.productTitle,
        row.productCode,
        row.externalDeliveryId,
        row.externalOrderId,
        row.deliveryNote,
        row.lineNumber,
      ]
        .join(' ')
        .toLocaleLowerCase();
      const date = row.deliveryDate.slice(0, 10);
      const period = this.period();
      // Date filtering must never hide saved or pending quantities, including cleared saved rows.
      const inPeriod =
        this.showAllDates() ||
        row.usedKg != null ||
        this.savedRowIds().has(row.deliveryLineId) ||
        ((!period.start || date >= period.start) && (!period.end || date <= period.end));
      const visibleActivity =
        row.isActive || row.usedKg != null || this.savedRowIds().has(row.deliveryLineId);
      return visibleActivity && inPeriod && terms.every((term) => text.includes(term));
    });
  });
  readonly dataSource = new FeedBalanceDataSource(toObservable(this.visibleRows));
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly saved = signal(false);
  private readonly original = signal('[]');
  readonly total = computed(() => this.rows().reduce((sum, row) => sum + (row.usedKg || 0), 0));
  readonly hasUnavailableAllocations = computed(() =>
    this.rows().some((row) => this.isUnavailableAllocation(row)),
  );
  readonly dirty = computed(() => JSON.stringify(this.allocations()) !== this.original());
  readonly valid = computed(() =>
    this.rows().every(
      (row) =>
        row.usedKg == null ||
        (Number.isFinite(row.usedKg) &&
          row.usedKg >= 0 &&
          row.usedKg <= Number.MAX_SAFE_INTEGER / 1000 &&
          Math.abs(row.usedKg - Math.round(row.usedKg * 1000) / 1000) < 1e-9),
    ),
  );

  constructor() {
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          this.loading.set(true);
          this.error.set('');
          this.saved.set(false);
          this.balance.set(null);
          this.rows.set([]);
          this.clearFilters();
          this.original.set('[]');
          const id = Number(params.get('id'));
          return this.backend.isPoultryEnabled && Number.isInteger(id) && id > 0
            ? this.service.get(id).pipe(
                catchError(() => {
                  this.error.set('feed-balance.load-error');
                  return of(null);
                }),
              )
            : of(null).pipe(
                map((): FeedBalance | null => {
                  this.error.set('feed-balance.load-error');
                  return null;
                }),
              );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((balance) => {
        if (balance) {
          this.apply(balance);
          if (this.selectedFlockId() !== balance.flockId) {
            this.store.dispatch(
              setSelectedFlockId({
                id: balance.flockId,
                save: true,
                reloadWeekData: true,
              }),
            );
          }
        }
        this.loading.set(false);
      });
  }

  allocations() {
    return this.rows()
      .filter((row) => row.usedKg != null && row.usedKg !== 0)
      .map((row) => ({ deliveryLineId: row.deliveryLineId, usedKg: row.usedKg! }));
  }

  isUnavailableAllocation(row: FeedBalanceRow): boolean {
    return (
      (row.usedKg ?? 0) > 0 &&
      (row.isMissing || !row.isActive) &&
      !this.savedRowIds().has(row.deliveryLineId)
    );
  }

  updateQuantity(id: number, value: number | null) {
    this.rows.update((rows) =>
      // Preserve row identity so table-v2 keeps the focused quantity input while typing.
      rows.map((row) => (row.deliveryLineId === id ? Object.assign(row, { usedKg: value }) : row)),
    );
    this.saved.set(false);
  }

  quantityInputValue(value: number | null): number | null {
    return Number.isFinite(value) ? value : null;
  }

  updateQuantityFromInput(id: number, input: HTMLInputElement) {
    this.updateQuantity(
      id,
      input.validity.badInput || input.value !== '' ? input.valueAsNumber : null,
    );
  }

  changePickerDate(boundary: 'start' | 'end', date: Date | null) {
    // Preserve the selected calendar day; UTC conversion can shift it across a date boundary.
    const value = date
      ? [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, '0'),
          String(date.getDate()).padStart(2, '0'),
        ].join('-')
      : '';
    this.changePeriod(boundary, value);
  }

  changePeriod(boundary: 'start' | 'end', value: string) {
    this.customPeriod.set({ ...this.period(), [boundary]: value });
    this.showAllDates.set(false);
  }

  clearFilters() {
    this.customPeriod.set(null);
    this.showAllDates.set(false);
    this.search.set('');
  }

  private apply(balance: FeedBalance) {
    this.balance.set(balance);
    // Group entered quantities on load/save only; preserve source order within each group.
    this.rows.set(
      balance.rows
        .map((row) => ({ ...row }))
        .sort((a, b) => Number((b.usedKg ?? 0) > 0) - Number((a.usedKg ?? 0) > 0)),
    );
    this.original.set(JSON.stringify(this.allocations()));
  }

  save() {
    const balance = this.balance();
    if (!balance || !this.valid() || this.hasUnavailableAllocations() || this.saving()) return;
    this.saving.set(true);
    this.error.set('');
    const request: SaveFeedBalance = {
      revision: balance.revision,
      allocations: this.allocations(),
    };
    this.service
      .save(balance.flockId, request)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.saving.set(false)),
      )
      .subscribe({
        next: (result) => {
          this.apply(result);
          this.saved.set(true);
        },
        error: (error: HttpErrorResponse) => this.handleSaveError(error),
      });
  }

  private handleSaveError(error: HttpErrorResponse): void {
    this.error.set(
      error.status === 409
        ? 'feed-balance.conflict'
        : error.status === 400 && error.error?.code === 'feed-balance.delivery-line-unavailable'
          ? 'feed-balance.delivery-line-unavailable'
          : error.status === 400 && error.error?.code === 'feed-balance.invalid-input'
            ? 'feed-balance.invalid-input'
            : 'feed-balance.save-error',
    );
    this.store.dispatch(
      NotificationActions.warningSnackbar({ message: this.locale.translate(this.error()) }),
    );
  }

  canLeave() {
    if (this.saving()) return false;
    if (!this.dirty()) return true;
    return this.dialog
      .confirmWarning({
        title: this.locale.translate('feed-balance.unsaved-title'),
        text: this.locale.translate('feed-balance.unsaved-message'),
        // The router owns this pending navigation; the dialog must not push/pop history.
        pushHistoryState: false,
      })
      .pipe(map((result) => result.status === DIALOG_RETURN_TYPES.confirm));
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent) {
    if (this.dirty() || this.saving()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  changeFlock(event: MatSelectChange) {
    this.router.navigate(['/flock/feed-balance', event.value]).then((changed) => {
      if (!changed) event.source.value = this.balance()?.flockId;
    });
  }

  cancel() {
    this.router.navigate(['/flock']);
  }
}
